import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { BoxComponentProps } from '../../base/box/Box';
import StaggerOnView from '../StaggerOnView';

type AnimationType = 'fadeInUp' | 'fadeInDown' | 'fadeInLeft' | 'fadeInRight';
type AnimationMode = 'standard' | 'cascade';

const CASCADE_STAGGER_DELAY = 0.25;

const initialTransformByType: Record<AnimationType, React.CSSProperties> = {
  fadeInUp: { transform: 'translate3d(0,-100px,0)' },
  fadeInDown: { transform: 'translate3d(0,100px,0)' },
  fadeInLeft: { transform: 'translate3d(-100px,0,0)' },
  fadeInRight: { transform: 'translate3d(100px,0,0)' },
};

const exitTransformByType: Record<AnimationType, React.CSSProperties> = {
  fadeInUp: { transform: 'translate3d(0,100px,0)' },
  fadeInDown: { transform: 'translate3d(0,-100px,0)' },
  fadeInLeft: { transform: 'translate3d(100px,0,0)' },
  fadeInRight: { transform: 'translate3d(-100px,0,0)' },
};

interface AnimateOnViewProps extends Omit<BoxComponentProps, 'children'> {
  fillContainer?: boolean;
  builderMode?: boolean;
  previewMode?: boolean;
  globalStyles?: { animation?: { enabled?: boolean; type?: AnimationType; mode?: AnimationMode } } | any;
  animateOnMount?: boolean;
  animateOnScroll?: boolean;
  enableExitAnimation?: boolean;
}

const AnimateOnView = ({
  fillContainer = false,
  builderMode,
  previewMode,
  globalStyles,
  animateOnMount = false,
  animateOnScroll = true,
  enableExitAnimation = false,
  children,
  style: incomingStyle,
  ...boxProps
}: PropsWithChildren<AnimateOnViewProps>) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [hasClientRendered, setHasClientRendered] = useState(false);

  // Track if we're currently animating to prevent interruptions
  const isAnimatingRef = useRef<boolean>(false);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastStateRef = useRef<'visible' | 'hidden'>('hidden');

  const animationEnabledFromGlobal = globalStyles?.animation?.enabled ?? false;

  const computedEnabled: boolean = (() => {
    if (previewMode === false && builderMode === false) return animationEnabledFromGlobal;
    if (previewMode === false) return false;
    if (builderMode) return Boolean(animationEnabledFromGlobal);
    return Boolean(animationEnabledFromGlobal);
  })();

  const resolvedType: AnimationType = (globalStyles?.animation?.type as AnimationType) || 'fadeInUp';
  const resolvedMode: AnimationMode = (globalStyles?.animation?.mode as AnimationMode) || 'standard';
  const cascadeEnabled = computedEnabled && resolvedMode === 'cascade';

  // Mark that we've reached the client so we can safely apply
  // JS-driven animation styles. When JS is disabled, this never
  // runs, so content remains fully visible (no opacity: 0).
  useEffect(() => {
    setHasClientRendered(true);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const ANIMATION_DURATION = 800; // Match CSS transition duration

    const markVisible = () => {
      // Don't interrupt an ongoing animation
      if (isAnimatingRef.current) {
        return;
      }

      // Only animate if we were previously hidden
      if (lastStateRef.current === 'hidden') {
        // Clear any pending timeout
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }

        isAnimatingRef.current = true;
        lastStateRef.current = 'visible';
        setIsExiting(false);
        setIsVisible(true);

        // Mark animation as complete after duration
        animationTimeoutRef.current = setTimeout(() => {
          isAnimatingRef.current = false;
        }, ANIMATION_DURATION);
      }
    };

    const markExiting = () => {
      // Don't interrupt an ongoing animation
      if (isAnimatingRef.current) {
        return;
      }

      // Only animate exit if we were previously visible
      if (lastStateRef.current === 'visible') {
        // Clear any pending timeout
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }

        if (!enableExitAnimation) {
          lastStateRef.current = 'hidden';
          setIsVisible(false);
          setIsExiting(false);
          return;
        }

        isAnimatingRef.current = true;
        lastStateRef.current = 'hidden';
        setIsVisible(false);
        setIsExiting(true);

        // Mark animation as complete after duration
        animationTimeoutRef.current = setTimeout(() => {
          isAnimatingRef.current = false;
          setIsExiting(false);
        }, ANIMATION_DURATION);
      }
    };

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!computedEnabled || prefersReducedMotion) {
      lastStateRef.current = 'visible';
      setIsVisible(true);
      return;
    }

    if (!animateOnScroll && animateOnMount) {
      markVisible();
      return;
    }

    const builderScroller = document.querySelector('[data-builder-container]') as HTMLElement | null;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Clear thresholds:
          // Enter when at least 25% visible
          // Exit when completely out of view
          const shouldEnter = entry.isIntersecting && entry.intersectionRatio >= 0.25;
          const shouldExit = !entry.isIntersecting && entry.intersectionRatio === 0;

          if (shouldEnter) {
            markVisible();

            if (!animateOnScroll) {
              observer.unobserve(entry.target);
            }
          } else if (animateOnScroll && shouldExit) {
            markExiting();
          }
        });
      },
      {
        root: builderScroller || null,
        // Use clear thresholds with hysteresis
        threshold: [0, 0.25],
        // Large margins to create buffer zones and prevent edge cases
        rootMargin: '50px 0px -50px 0px',
      }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [computedEnabled, animateOnMount, animateOnScroll, enableExitAnimation]);

  if (cascadeEnabled) {
    return (
      <StaggerOnView
        staggerDelay={CASCADE_STAGGER_DELAY}
        builderMode={builderMode}
        previewMode={previewMode}
        globalStyles={globalStyles}
        animateOnMount={animateOnMount}
        animateOnScroll={animateOnScroll}
        {...boxProps}
      >
        {children}
      </StaggerOnView>
    );
  }

  const baseStyle: React.CSSProperties = { opacity: 0, willChange: 'opacity, transform' };
  const visibleStyle: React.CSSProperties = { opacity: 1, transform: 'translate3d(0,0,0)' };
  const transition = 'opacity 0.8s ease-in-out, transform 0.8s ease-in-out';

  const containerStyles = fillContainer
    ? {
        width: '100%',
        height: '100%',
      }
    : {};

  const shouldAnimate = hasClientRendered && computedEnabled;

  const animationStyle: React.CSSProperties = shouldAnimate
    ? {
        ...containerStyles,
        ...(isVisible
          ? visibleStyle
          : isExiting
          ? { ...baseStyle, ...exitTransformByType[resolvedType] }
          : { ...baseStyle, ...initialTransformByType[resolvedType] }),
        transition,
      }
    : containerStyles;

  const style: React.CSSProperties = {
    ...(incomingStyle || {}),
    ...animationStyle,
  };

  const setContainerRef = (el: HTMLDivElement | null) => {
    containerRef.current = el;
  };

  const { ref: _ignoredRef, ...restBoxProps } = boxProps as { ref?: unknown };

  return (
    <Box ref={setContainerRef} style={style} {...(restBoxProps as BoxComponentProps)}>
      {children}
    </Box>
  );
};

export default AnimateOnView;
